import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import type { ItemStatus } from '../hooks/use-item-validation-form';

interface ItemStatusSelectorProps {
  status: ItemStatus;
  onStatusChange: (status: ItemStatus) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  showReasonError?: boolean;
}

function requiresReason(status: ItemStatus): boolean {
  return status === 'ACCEPTED_WITH_RESERVATION' || status === 'REJECTED';
}

export function ItemStatusSelector({
  status,
  onStatusChange,
  reason,
  onReasonChange,
  showReasonError = false,
}: ItemStatusSelectorProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const showReason = requiresReason(status);
  const hasReasonError = showReasonError && showReason && reason.trim() === '';

  const StatusOptions: { value: ItemStatus; label: string }[] = [
    { value: 'ACCEPTED', label: t('goodsReceiving.accepted') },
    { value: 'ACCEPTED_WITH_RESERVATION', label: t('goodsReceiving.acceptedWithReservation') },
    { value: 'REJECTED', label: t('goodsReceiving.rejected') },
  ];

  return (
    <>
      <FormControl component="fieldset">
        <FormLabel component="legend">{t('goodsReceiving.itemStatus')}</FormLabel>
        <RadioGroup value={status} onChange={(e) => onStatusChange(e.target.value as ItemStatus)}>
          {StatusOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {showReason && (
        <TextField
          label={t('goodsReceiving.reason')}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          fullWidth
          required
          multiline
          rows={3}
          slotProps={{ input: { style: { fontSize: isMobile ? '16px' : undefined } } }}
          error={hasReasonError}
          helperText={hasReasonError ? t('goodsReceiving.reasonRequired') : undefined}
        />
      )}
    </>
  );
}
